import axios from 'axios';
import axiosRetry from 'axios-retry';
import Vue from 'vue';
import { GET_VALUE } from '../store/modules/session/getter-types';
import { SESSION } from '../store/types';
import { SESSION_STATE_KEY } from '../store/modules/session/keys';
import { getLogger } from './../logger/logger';
import EventEmitter from '../lib/EventEmitter';
import sessionManager from '../auth/session/sessionManager';
import { CRUDMethods, EVENTS } from './consts';

const logger = getLogger('LiveEngage');
const CancelToken = axios.CancelToken;
const DEFAULTS = {
  contentType: {
    JSON: 'application/json',
  },
};

const DEFAULT_HEADERS = {
  Accept: '*/*',
  'Content-Type': DEFAULTS.contentType.JSON,
};

function loadDefaultHeaders(headers = {}) {
  return Object.assign({}, DEFAULT_HEADERS, headers);
}

function loadAuthHeaders(headers) {
  const toExtend = headers || {};
  const store = new Vue().$store;
  const glob = store.getters[`${SESSION}/${GET_VALUE}`](SESSION_STATE_KEY.GLOB);
  if (glob) {
    Object.assign(toExtend, { Authorization: `Bearer ${glob}` });
  }
  return toExtend;
}

function defaultErrorHandler(error) {
  /* eslint-disable no-underscore-dangle */
  if (error) {
    if (axios.isCancel(error)) {
      logger.debug('This request has been CANCELED.... SEE YA, NOTHING TO DO...');
    } else if (error.response && error.response.status === 401) {
      logger.debug('BaseClient:errorHandler, response with status code 500 try sustain');
      sessionManager.sustain({ async: false });
    }
  }
  return Promise.reject(error);
}

function loadResponseInterceptors(instance, errorHandler = defaultErrorHandler) {
  instance.interceptors.response.use(response => response, errorHandler);
}


/**
 * Add a random number to the request to
 * make sure we don't get the cached browser
 * response back
 * @param request
 */

function addBusterToQueryString(request) {
  const buster = Math.floor(Math.random() * 100000);
  request.params = Object.assign({}, request.params, { __d: buster });
  return request;
}

function loadRequestInterceptors(instance, { addBuster = true }) {
  if (addBuster) {
    instance.interceptors.request.use(request => addBusterToQueryString(request));
  }
}

/**
 * Create BaseHttpService extends EventEmitter.
 * http client for AC.
 *
 * @param {Object} options - config object.
 * @param {Object} options.allowedMethods - list of method to expose.
 * @param {Object} options.config.headers - headers.
 * @param {Object} options.retryConfig - retry configure
 * @param {Object} options.retryConfig.retry - number of retries (default 3)
 * should be reset between retries
 * @param {Object} options.retryConfig.retryCondition - A function to determine if
 * the error can be retried
 * @param {Object} options.retryConfig.retryDelay - A function to determine the
 * delay between retry requests
 *
 */


/* eslint-disable no-param-reassign  */
/* eslint-disable no-shadow  */
export default class BaseClient extends EventEmitter {
  constructor(options) {
    if (!options) {
      throw new Error('BaseClient must get options for basic config');
    }
    options.allowedMethods = options.allowedMethods || [CRUDMethods.GET];

    super();
    this.options = options;
    this.options.config.headers = loadAuthHeaders(loadDefaultHeaders(options.config.headers));
    const config = Object.assign({}, options.config, {
      validateStatus: status => (status >= 200 && status < 300) || status === 304,
    });
    this.instance = axios.create(config);
    axiosRetry(this.instance, options.retryConfig || {});
    options.allowedMethods.forEach((opt) => {
      this[opt] = this.instance[opt];
    });
    loadResponseInterceptors(this.instance, options.errorHandler);
    loadRequestInterceptors(this.instance, options);
  }

  getBaseUri() {
    return this.instance.defaults.baseURL;
  }

  getBaseDefaults() {
    return this.instance.defaults;
  }

  useRequestInterceptor(interceptor) {
    this.instance.interceptors.request.use(interceptor);
  }

  useResponseInterceptor(interceptor) {
    this.instance.interceptors.response.use(interceptor);
  }

  /**
   * unsubscribe
   * use axios cancel obj, clear all timeouts.
   * @param {Object} cancelObject - config object (return object of subscription).
   *
   */
  unsubscribe(cancelObject) {
    if (!cancelObject) {
      logger.error('BaseClient:unsubscribe, cancelObject is required');
      throw new Error('BaseClient:unsubscribe, cancelObject is required');
    } else if (cancelObject.cancel) {
      cancelObject.cancel();
    }
    return clearTimeout(this.pollingTimeoutRef);
  }

  /**
   * subscribe
   * subscribe and get events....
   *
   * @param {Object} options - config object.
   * @param {Number} options.interval - interval.
   * @param {Number} options.retries - number of retries (if set to -1 will fail for ever).
   * @param {String} options.method - crud method.
   * @param {String} options.uri - uri.
   * @param {Object} options.body - body to send with the request.
   * @param {Object} options.config - config for axios.
   *
   */
  subscribe(options = {}) {
    const source = CancelToken.source();
    const interval = options.interval || 10000;
    const method = options.method || CRUDMethods.GET;
    const uri = options.uri || undefined;
    const body = options.body || undefined;
    const config = options.config || undefined;

    Object.assign(config || {}, { cancelToken: source.token });

    const pollingOptions = {
      interval,
      method,
      cancelToken: source.token,
    };

    async function pollingLoop(uri, body, config) {
      const context = this;
      try {
        const res = await context.instance[pollingOptions.method](uri, body || config,
          config).catch((e) => {
          context.unsubscribe(pollingOptions.cancelToken);
          context.emit(EVENTS.onSubscriptionError, e);
          logger.error(`pollingLoop: ${e}`);
        });
        if (res) {
          context.emit(EVENTS.onSubscriptionUpdateNotification, res);
          context.pollingTimeoutRef = setTimeout(pollingLoop.bind(context, uri, body,
            config), pollingOptions.interval);
        }
      } catch (e) {
        context.unsubscribe(pollingOptions.cancelToken);
        context.emit(EVENTS.onSubscriptionError, e);
        logger.error(`pollingLoop: ${e}`);
      }
    }

    pollingLoop.call(this, uri, body, config);
    return source;
  }
}



// WEBPACK FOOTER //
// ./src/api-layer/BaseClient.js